/**
 * Sessão do jogador, sobre o Supabase Auth.
 *
 * A chave publicável do Supabase é pública por natureza — ela identifica o
 * projeto, não autoriza nada. Quem decide o que cada pessoa pode ver é a
 * RLS, e quem move dinheiro são as funções SQL, chamadas só pelo Worker com
 * a chave secreta. Nada disso muda por a chave estar no bundle.
 *
 * O token de acesso desta sessão é o que o motor de jogo verifica pelo JWKS
 * do projeto: o navegador não prova identidade, o token prova.
 */
import { type Session, type SupabaseClient, createClient } from '@supabase/supabase-js';

let cliente: SupabaseClient | null = null;

/** Configuração ausente significa modo demonstração, sem backend. */
export function supabaseConfigurado(): boolean {
  return Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);
}

export function supabase(): SupabaseClient {
  if (!supabaseConfigurado()) {
    throw new Error('Supabase não configurado: defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY');
  }
  cliente ??= createClient(
    import.meta.env.VITE_SUPABASE_URL as string,
    import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        /**
         * O padrão do auth-js é `implicit`, que devolve access_token,
         * refresh_token e o token do Google no fragmento da URL depois do
         * login. Fragmento de URL vai para o histórico do navegador, para
         * captura de tela, e para qualquer copiar-colar — e o
         * refresh_token não expira em uma hora como o access_token: ele
         * gera sessões novas até alguém revogar.
         *
         * Com `pkce` o provedor devolve só um código de uso único, que o
         * cliente troca por token num POST. Nada sensível aparece na
         * barra de endereço em momento nenhum.
         *
         * Não exige mudança no painel do Supabase nem no Google: a URL de
         * retorno é a mesma.
         */
        flowType: 'pkce',
      },
    },
  );
  return cliente;
}

export type EstadoSessao =
  | { readonly estado: 'carregando' }
  | { readonly estado: 'sem-backend' }
  | { readonly estado: 'deslogado' }
  | { readonly estado: 'logado'; readonly sessao: Session };

export interface ResultadoAuth {
  readonly ok: boolean;
  readonly mensagem?: string;
  /** Cadastro que exige confirmação por e-mail antes de entrar. */
  readonly precisaConfirmar?: boolean;
}

/**
 * Traduz o erro do Supabase para algo que o jogador entenda e possa agir.
 * A mensagem crua é em inglês e fala de implementação.
 */
function traduzir(mensagem: string): string {
  const m = mensagem.toLowerCase();
  if (m.includes('invalid login credentials')) return 'E-mail ou senha não conferem.';
  if (m.includes('email not confirmed')) return 'Confirme o e-mail antes de entrar.';
  if (m.includes('user already registered')) return 'Esse e-mail já tem conta. Tente entrar.';
  if (m.includes('password should be at least')) {
    return 'A senha precisa de pelo menos 6 caracteres.';
  }
  if (m.includes('unable to validate email')) return 'E-mail inválido.';
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Muitas tentativas seguidas. Espere um minuto.';
  }
  if (m.includes('provider is not enabled') || m.includes('unsupported provider')) {
    return 'A entrada pelo Google ainda não está ativada neste projeto.';
  }
  return mensagem;
}

export async function entrar(email: string, senha: string): Promise<ResultadoAuth> {
  const { error } = await supabase().auth.signInWithPassword({ email, password: senha });
  return error ? { ok: false, mensagem: traduzir(error.message) } : { ok: true };
}

export interface DadosCadastro {
  readonly nome: string;
  /** ISO `AAAA-MM-DD`. */
  readonly nascimento: string;
  /** Só dígitos ou com pontuação: o banco normaliza. */
  readonly cpf: string;
}

export async function cadastrar(
  email: string,
  senha: string,
  dados?: DadosCadastro,
): Promise<ResultadoAuth> {
  // Sem `emailRedirectTo`, o link de confirmação vai para o Site URL do
  // projeto — que nasce apontando para localhost e leva quem se cadastrou
  // a uma página que não existe na máquina dele. Mandando a origem atual,
  // o link volta para o mesmo endereço onde a pessoa se cadastrou.
  //
  // O Supabase só honra esta origem se ela constar do Site URL ou da lista
  // de redirecionamentos permitidos do projeto; fora dela, cai no Site URL
  // de novo. Ou seja: isto resolve metade do problema, e a outra metade é
  // configuração no painel.
  const origem = globalThis.location?.origin;

  // Identidade viaja como metadado do cadastro, e não numa chamada
  // seguinte. É uma questão de ordem, não de conveniência: a conta só
  // ganha sessão depois de o e-mail ser confirmado, e sem sessão não há
  // como chamar a rota autenticada que declara identidade. Do outro lado,
  // o gatilho de provisionamento lê o metadado e chama a mesma
  // `fn_identity_declare` de sempre — dígito verificador, CPF único,
  // maioridade, e `kyc_status` parando em PENDING.
  //
  // Se o CPF já pertencer a outra conta, a declaração falha em silêncio e
  // a conta nasce sem identidade. Não é perda: o aplicativo pergunta de
  // novo na Carteira, e aí sim com a mensagem certa.
  const { data, error } = await supabase().auth.signUp({
    email,
    password: senha,
    options: {
      ...(origem ? { emailRedirectTo: origem } : {}),
      ...(dados
        ? {
            data: {
              display_name: dados.nome.trim(),
              birth_date: dados.nascimento,
              cpf: dados.cpf.replace(/\D/g, ''),
            },
          }
        : {}),
    },
  });
  if (error) return { ok: false, mensagem: traduzir(error.message) };

  // Sem sessão na resposta, o projeto exige confirmação por e-mail.
  return { ok: true, precisaConfirmar: data.session === null };
}

/**
 * Entrada pelo Google.
 *
 * Quem entra por aqui não passa por formulário nenhum, então chega sem
 * data de nascimento e sem CPF — e é por isso que a coleta de identidade
 * continua existindo como passo dentro do aplicativo. O cadastro por
 * e-mail apenas a antecipa; ele não a substitui.
 *
 * O nome vem do provedor e vira nome de tela, cortado no limite da
 * coluna. É o único dado do Google que a plataforma guarda: não pedimos
 * agenda, contatos nem nada além do perfil básico.
 */
export async function entrarComGoogle(): Promise<ResultadoAuth> {
  const origem = globalThis.location?.origin;
  const { error } = await supabase().auth.signInWithOAuth({
    provider: 'google',
    ...(origem ? { options: { redirectTo: origem } } : {}),
  });
  return error ? { ok: false, mensagem: traduzir(error.message) } : { ok: true };
}

export async function sair(): Promise<void> {
  await supabase().auth.signOut();
}
